import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useTranslation } from '../../utils/translations';
import { useAuth } from '../../context/AuthContext';
import { menuItemsConfig } from './Sidebar';
import supabase from '../../lib/supabase';

const { FiMenu, FiChevronRight, FiHeadphones } = FiIcons;

const MobileBottomNav = ({ activeTab, setActiveTab }) => {
  const { user } = useAuth();
  const t = useTranslation(user?.language || 'fr'); // Default to French
  const touchStartX = useRef(null);
  const mobileMenuRef = useRef(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [appLogo, setAppLogo] = useState('https://ui-avatars.com/api/?name=AI&background=007ee5&color=fff&size=200&bold=true');

  const toggleMobileMenu = () => {
    console.log('Toggling mobile menu, current state:', isMobileMenuOpen);
    // Add slight delay to ensure animation completes
    setTimeout(() => {
      setIsMobileMenuOpen(prev => {
        const newState = !prev;
        console.log('New mobile menu state:', newState);
        return newState;
      });
    }, 100);
  };

  // Fetch app logo from settings + realtime updates
  useEffect(() => {
    const fetchAppLogo = async () => {
      try {
        const { data, error } = await supabase
          .from('app_settings_a1b2c3d4e5')
          .select('value')
          .eq('key', 'app_logo')
          .single();
        if (error) throw error;
        if (data && data.value) setAppLogo(data.value);
      } catch (error) {
        console.error('Error fetching app logo:', error);
      }
    };
    fetchAppLogo();

    const channel = supabase
      .channel('app_settings_changes')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'app_settings_a1b2c3d4e5',
        filter: 'key=eq.app_logo'
      }, (payload) => {
        if (payload.new?.value) setAppLogo(payload.new.value);
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        console.log('Clicked outside, closing mobile menu');
        // Delay to prevent interrupting animation
        setTimeout(() => {
          setIsMobileMenuOpen(false);
        }, 100);
      }
    };
    if (isMobileMenuOpen) {
      console.log('Adding click outside listener');
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      console.log('Removing click outside listener');
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Handle swipe gestures for navigation
  useEffect(() => {
    const handleTouchStart = (event) => {
      touchStartX.current = event.touches[0].clientX;
      console.log('Touch start:', touchStartX.current);
    };

    const handleTouchEnd = (event) => {
      if (touchStartX.current === null) return;
      const touchEndX = event.changedTouches[0].clientX;
      const deltaX = touchEndX - touchStartX.current;
      const swipeThreshold = 50;
      console.log('Touch end:', touchEndX, 'Delta:', deltaX);

      const bottomNavItems = menuItemsConfig(t, user).filter(item =>
        item.render && ['agents', 'statistics', 'help-center'].includes(item.id)
      );
      const currentIndex = bottomNavItems.findIndex(item => item.id === activeTab);

      if (deltaX > swipeThreshold && currentIndex > 0) {
        console.log('Swiping right, setting tab:', bottomNavItems[currentIndex - 1].id);
        setActiveTab(bottomNavItems[currentIndex - 1].id);
      } else if (deltaX < -swipeThreshold && currentIndex < bottomNavItems.length - 1) {
        console.log('Swiping left, setting tab:', bottomNavItems[currentIndex + 1].id);
        setActiveTab(bottomNavItems[currentIndex + 1].id);
      }

      touchStartX.current = null;
    };

    window.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('touchend', handleTouchEnd);
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [activeTab, setActiveTab, t, user]);

  const bottomNavItems = menuItemsConfig(t, user).filter(item => 
    item.render && ['agents', 'statistics', 'help-center'].includes(item.id)
  );

  const moreMenuItems = menuItemsConfig(t, user).filter(item => 
    item.render && !['agents', 'statistics', 'help-center'].includes(item.id)
  );

  const handleSupportClick = () => {
    console.log('Support button clicked, opening prosomo.com');
    window.open('https://prosomo.com', '_blank');
  };

  return (
    <>
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 z-50 shadow-lg mobileBottomBar">
        <div className="flex justify-around items-center py-2">
          {bottomNavItems.map((item) => (
            <motion.button
              key={item.id}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => {
                console.log('Clicked bottom nav item:', item.id);
                setActiveTab(item.id);
              }}
              className={`flex flex-col items-center px-2 py-1 text-sm ${activeTab === item.id ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`}
              title={item.label}
            >
              <SafeIcon
                icon={item.icon}
                className={`w-6 h-6 ${activeTab === item.id ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`}
              />
              <span className="text-xs mt-1">{item.label}</span>
            </motion.button>
          ))}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => {
              console.log('Menu button clicked, toggling mobile menu');
              toggleMobileMenu();
            }}
            className={`flex flex-col items-center px-2 py-1 text-sm ${isMobileMenuOpen ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`}
            title={t('sideBar.menu', 'Menu')}
          >
            <SafeIcon
              icon={FiMenu}
              className={`w-6 h-6 ${isMobileMenuOpen ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`}
            />
            <span className="text-xs mt-1">{t('sideBar.menu', 'Menu')}</span>
          </motion.button>
        </div>
      </div>

      {/* Mobile Drawer Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            ref={mobileMenuRef}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 200, damping: 25, duration: 0.3 }}
            onAnimationStart={() => console.log('Drawer animation started:', isMobileMenuOpen ? 'opening' : 'closing')}
            onAnimationComplete={() => console.log('Drawer animation completed:', isMobileMenuOpen ? 'opened' : 'closed')}
            className="lg:hidden fixed top-0 right-0 bottom-0 w-64 bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 z-[60] rounded-l-2xl shadow-2xl"
          >
            {/* Header */}
            <div className="p-4 flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-lg overflow-hidden flex items-center justify-center">
                  <img
                    src={appLogo}
                    alt={t('sideBar.appLogoAlt', 'Prosomo')}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = "https://ui-avatars.com/api/?name=AI&background=007ee5&color=fff&size=200&bold=true";
                    }}
                  />
                </div>
                <h1 className="text-lg font-bold text-gray-900 dark:text-white">
                  {t('sideBar.appName', 'Prosomo')}
                </h1>
              </div>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => {
                  console.log('Close button clicked, toggling mobile menu');
                  toggleMobileMenu();
                }}
                className="text-gray-600 dark:text-gray-400"
              >
                <SafeIcon icon={FiChevronRight} className="w-6 h-6" />
              </motion.button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto pb-20 relative">
              {process.env.NODE_ENV === 'development' && user && (
                <div className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-xs">
                  <div className="text-gray-600 dark:text-gray-400">
                    {t('sideBar.roleLabel', 'Role:')} <span className="font-mono text-blue-600 dark:text-blue-400">{user.role}</span>
                  </div>
                  <div className="text-gray-600 dark:text-gray-400">
                    {t('sideBar.idLabel', 'ID:')} <span className="font-mono text-gray-500">{user.id?.slice(0)}...</span>
                  </div>
                </div>
              )}

              {/* Navigation Menu */}
              <nav className="p-4">
                <ul className="space-y-2">
                  {moreMenuItems.map((item) => (
                    <li key={item.id}>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          console.log('Clicked drawer menu item:', item.id);
                          setActiveTab(item.id);
                          setIsMobileMenuOpen(false);
                        }}
                        className={`w-full flex items-center justify-start px-4 py-3 rounded-xl text-left transition-all ${activeTab === item.id ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'}`}
                      >
                        <SafeIcon
                          icon={item.icon}
                          className={`w-5 h-5 ${activeTab === item.id ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}
                        />
                        <span className="ml-3 font-medium">
                          {item.label}
                        </span>
                      </motion.button>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>

            {/* Fixed Contact Support Section */}
            <div className="fixed bottom-0 w-64 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 z-10">
              <div className="p-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                      <SafeIcon icon={FiHeadphones} className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                        {t("sideBar.technicalSupport", "Technical Support")}
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t("sideBar.needHelp", "Need help?")}
                      </p>
                    </div>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSupportClick}
                    className="w-full bg-blue-500 text-white text-sm font-medium py-2 px-3 rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    {t("sideBar.contactSupport", "Contact Support")}
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default MobileBottomNav;